import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Util } from "@common/utils/util";
import { ACCOUNTS_BALANCE_FREEZING } from "@constants/business.constant";
import {
  CONFIRM_SOLICITUD_ERROR,
  SOLICITUD_CONGELAMIENTO_SALDO_RESPONSE,
} from "@constants/congelamiento-saldo";
import {
  INICIO,
  ROUTING_CONGELAMIENTOS_SALDOS
} from "@constants/url.constant";
import { ModalService } from "@services/modal/modal.service";
import { StepsService } from "@services/steps/steps.service";
import { StorageService } from "@services/storage/storage.service";
import { GenericErrorOps } from "@shared/components/generic-error/interfaces/generic-error-ops.interface";
import { firstValueFrom, Subscription } from "rxjs";
import { CongelamientoSaldosTerminosYCondicionesModalComponent } from "../../components/congelamiento-saldos-terminos-y-condiciones-modal/congelamiento-saldos-terminos-y-condiciones-modal.component";
import { CongelamientoSaldoService } from "../../services/congelamiento-saldo.service";

@Component({
    selector: "app-congelamiento-saldos-confirmar",
    templateUrl: "./congelamiento-saldos-confirmar.component.html",
    styleUrls: ["./congelamiento-saldos-confirmar.component.scss"],
    standalone: false
})
export class CongelamientoSaldosConfirmarComponent
  implements OnInit, OnDestroy
{
  public loading: boolean = false;
  public error: boolean = false;
  public enableButtonRetry: boolean = false;
  public errorOps: GenericErrorOps;
  public ccoSelectedAmount: number = 0;
  public cdcSelectedAmount: number = 0;
  public ccvSelectedAmount: number = 0;
  public cavSelectedAmount: number = 0;
  public ccvAmount: number = 0;
  public cavAmount: number = 0;
  public acceptedTerms: boolean = false;
  private readonly sub: Subscription = new Subscription();
  private accounts_balance_freezing: any;
  private token: string;
  isError: boolean = false;
  attempt: boolean = false;
  nAttempts: number = 0;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly stepsService: StepsService,
    private readonly storageService: StorageService,
    private readonly modalService: ModalService,
    private readonly congelamientoSaldoService: CongelamientoSaldoService,
    private readonly utils: Util
  ) {
    stepsService.setCurrentStep(2);
    this.sub.add(
      this.modalService.clickAceptModal.subscribe((res) => {
        this.acceptedTerms = res;
      })
    );
  }

  ngOnInit() {
    void this.initializeComponent();
  }
  private async initializeComponent() {
    this.token = await this.utils.getIdToken();
    this.acceptedTerms = false;
    this.accounts_balance_freezing = await this.storageService.get(
      ACCOUNTS_BALANCE_FREEZING
    );
    this.ccoSelectedAmount = this.accounts_balance_freezing.ccoSelectedAmount;
    this.cdcSelectedAmount = this.accounts_balance_freezing.cdcSelectedAmount;
    this.ccvSelectedAmount = this.accounts_balance_freezing.ccvSelectedAmount;
    this.cavSelectedAmount = this.accounts_balance_freezing.cavSelectedAmount;
    this.ccvAmount = this.accounts_balance_freezing.ccvAmount;
    this.cavAmount = this.accounts_balance_freezing.cavAmount;
  }

  public isAnyObligatoryAccountWithSelectedFunds(): boolean {
    return this.ccoSelectedAmount !== 0 || this.cdcSelectedAmount !== 0;
  }

  public isAnyVoluntaryAccountWithSelectedFunds(): boolean {
    return this.ccvSelectedAmount !== 0 || this.cavSelectedAmount !== 0;
  }

  public numberToChileanCurrency(value: number): string {
    return this.utils.formatCurrency(value);
  }

  getTotalObligatorySelectedFunds(): string {
    const totalObligatorySelectedFunds =
      this.ccoSelectedAmount + this.cdcSelectedAmount;
    return this.numberToChileanCurrency(totalObligatorySelectedFunds);
  }

  getTotalVoluntarySelectedFunds(): string {
    const totalVoluntarySelectedFunds =
      this.ccvSelectedAmount + this.cavSelectedAmount;
    return this.numberToChileanCurrency(totalVoluntarySelectedFunds);
  }

  getTotalSelectedFunds(): string {
    const totalSelected =
      this.ccoSelectedAmount +
      this.cdcSelectedAmount +
      this.ccvSelectedAmount +
      this.cavSelectedAmount;
    return this.numberToChileanCurrency(totalSelected);
  }

  get ccvSelectedPercentage(): string {
    const percentage = Math.trunc(
      (this.ccvSelectedAmount * 100) / this.ccvAmount
    );
    return percentage + "%";
  }

  get cavSelectedPercentage(): string {
    const percentage = Math.trunc(
      (this.cavSelectedAmount * 100) / this.cavAmount
    );
    return percentage + "%";
  }

  openTermsAndConditionsModal() {
    void this.modalService.openCustomModal({
      component: CongelamientoSaldosTerminosYCondicionesModalComponent,
      cssClass: "generic-modal",
    });
  }

  async continue() {
    try {
      this.loading = true;
      this.storageService.set("clientHasAcceptedTheTerms", this.acceptedTerms);
      const data = this.getDataRequest();
      const response = await firstValueFrom(
        this.congelamientoSaldoService.solicitudCambioViaPago(this.token, data)
      );
      response.subject = "Congelamiento de saldos";
      this.storageService.set(SOLICITUD_CONGELAMIENTO_SALDO_RESPONSE, response);

      if (response) {
        this.isError = false;
        this.router.navigate([
          ROUTING_CONGELAMIENTOS_SALDOS.CONGELAMIENTO_SALDOS_BASE,
          ROUTING_CONGELAMIENTOS_SALDOS.CONGELAMIENTO_SALDOS_URL,
          ROUTING_CONGELAMIENTOS_SALDOS.CONGELAMIENTO_SALDOS_COMPROBANTE,
        ]);
      }
    } catch (error) {
      console.error('Error in continue():', error);
      this.errorOps = CONFIRM_SOLICITUD_ERROR;
      this.errorImplement();
    } finally {
      this.loading = false;
    }
  }
  getDataRequest() {
    const keysOfInterest: string[] = [
      "ccoSelectedAmount",
      "cdcSelectedAmount",
      "ccvSelectedAmount",
      "cavSelectedAmount",
      "cavSelectedAmountPercentage",
      "ccvSelectedAmountPercentage",
    ];

    const dataResult: any = {};

    for (const clave of keysOfInterest) {
      const value = this.accounts_balance_freezing[clave];
      if (value > 0 && value != null) {
      dataResult[clave] = Math.round(value);
      }
    }

    const selectedTotalAmount =
      this.accounts_balance_freezing?.["selectedTotalAmount"];
    if (selectedTotalAmount != null) {
      dataResult["selectedTotalAmount"] = Math.round(selectedTotalAmount);
    }

    const ccvAmount = this.accounts_balance_freezing?.["ccvAmount"];
    if (ccvAmount != null) {
      dataResult["ccvSelectedTotalBalance"] = Math.round(ccvAmount);
    }

    const cavAmount = this.accounts_balance_freezing?.["cavAmount"];
    if (cavAmount != null) {
      dataResult["cavSelectedTotalBalance"] = Math.round(cavAmount);
    }

    dataResult["acceptedTerms"] = this.acceptedTerms;

    return dataResult;
  }

  async exit() {
    await this.modalService.congelamientoSaldosExitModal();
  }

  errorImplement() {
    if (this.nAttempts === 3) {
      this.attempt = false;
      this.isError = false;
      this.nAttempts = 0;
      this.router.navigate([
        ROUTING_CONGELAMIENTOS_SALDOS.CONGELAMIENTO_SALDOS_BASE,
        ROUTING_CONGELAMIENTOS_SALDOS.CONGELAMIENTO_SALDOS_URL,
        ROUTING_CONGELAMIENTOS_SALDOS.CONGELAMIENTO_SALDOS_HOME,
      ]);
      this.stepsService.setShowStepper(false);
    }
    this.loading = false;
    this.isError = true;
    this.attempt = false;
    this.stepsService.setShowStepper(false);
  }

  async onRetryEvent() {
    this.attempt = true;
    this.nAttempts++;
    if (this.nAttempts <= 3) await this.continue();
  }

  onGoToEvent() {
    this.router.navigate([INICIO]);
    this.stepsService.setShowStepper(true);
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
